import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Anchor, ArrowLeft, CheckCircle2, Copy, FileDown, GraduationCap, Link2, Loader2, Plus, Trash2, Upload, X, XCircle } from "lucide-react";
import { API, api, formatApiError } from "../lib/api";
import { inputCls, labelCls } from "../components/Modals";

function CompileDraft({ draft, onStatus }) {
    let c = null;
    try { c = JSON.parse(draft.innehall); } catch { /* ignore */ }
    return (
        <div data-testid={`compile-draft-${draft.id}`} className="rounded-xl border border-aidraft/30 bg-aidraft/5 p-4 space-y-3">
            <div className="flex items-center justify-between">
                <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${draft.status === "utkast" ? "text-aidraft border-aidraft/40 bg-aidraft/10" : draft.status === "godkand" ? "text-buoy border-buoy/40 bg-buoy/10" : "text-slate-500 border-slate-600"}`}>
                    {draft.status === "utkast" ? "AI-utkast – kräver granskning" : draft.status === "godkand" ? "Godkänd" : "Avvisad"}
                </span>
                <span className="font-mono text-[9px] text-slate-600">{new Date(draft.created_at).toLocaleString("sv-SE")}</span>
            </div>
            {c && (
                <>
                    <p className="text-sm text-slate-300 leading-relaxed">{c.sammanfattning}</p>
                    {(c.teman || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {c.teman.map((t, i) => (
                                <span key={i} title={t.beskrivning} className="text-[11px] bg-current/10 text-current border border-current/30 rounded-full px-2.5 py-0.5">
                                    {t.titel}{t.antal_roster ? ` (${t.antal_roster})` : ""}
                                </span>
                            ))}
                        </div>
                    )}
                    {(c.svar_per_fraga || []).map((f, i) => (
                        <div key={i} className="rounded-lg bg-abyss/40 border border-border p-3">
                            <p className="text-xs font-semibold text-slate-200 mb-1">{f.fraga}</p>
                            <p className="text-xs text-slate-400 leading-relaxed">{f.sammanfattning}</p>
                            {(f.representativa_svar || []).slice(0, 3).map((s, j) => (
                                <p key={j} className="text-[11px] text-slate-500 italic mt-1">"{s}"</p>
                            ))}
                        </div>
                    ))}
                    {(c.reflektioner || []).length > 0 && (
                        <ul className="space-y-1">
                            {c.reflektioner.map((r, i) => <li key={i} className="text-xs text-slate-400">→ {r}</li>)}
                        </ul>
                    )}
                </>
            )}
            {draft.status === "utkast" && (
                <div className="flex gap-2">
                    <button data-testid={`compile-approve-${draft.id}`} onClick={() => onStatus(draft.id, "godkand")}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy py-1.5 text-xs font-semibold hover:bg-buoy/25 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Godkänn
                    </button>
                    <button data-testid={`compile-reject-${draft.id}`} onClick={() => onStatus(draft.id, "avvisad")}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-slate-600 text-slate-400 py-1.5 text-xs hover:border-red-500/50 hover:text-red-400 transition-colors">
                        <XCircle className="w-3.5 h-3.5" /> Avvisa
                    </button>
                </div>
            )}
        </div>
    );
}

export default function Utbildning() {
    const [trainings, setTrainings] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [newForm, setNewForm] = useState({ title: "", datum: "", description: "" });
    const [fragor, setFragor] = useState([""]);
    const [timmar, setTimmar] = useState(72);
    const [newLink, setNewLink] = useState(null);
    const [busy, setBusy] = useState(null);
    const [openResponses, setOpenResponses] = useState(false);

    const loadList = () => api.get("/trainings").then((r) => setTrainings(r.data)).catch(() => {});
    const loadDetail = (tid) => api.get(`/trainings/${tid}`).then((r) => setDetail(r.data)).catch(() => {});

    useEffect(() => { loadList(); }, []);
    useEffect(() => { if (selected) loadDetail(selected); }, [selected]);

    const createTraining = async () => {
        if (!newForm.title.trim()) return;
        const r = await api.post("/trainings", newForm);
        toast.success("Utbildning skapad");
        setNewForm({ title: "", datum: "", description: "" });
        setShowNew(false);
        await loadList();
        setSelected(r.data.id);
    };

    const uploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selected) return;
        setBusy("upload");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/trainings/${selected}/materials`, fd);
            toast.success("Material uppladdat");
            loadDetail(selected);
        } catch (err) {
            toast.error(formatApiError(err, "Uppladdningen misslyckades"));
        } finally {
            setBusy(null);
            e.target.value = "";
        }
    };

    const deleteMaterial = async (mid) => {
        await api.delete(`/training-materials/${mid}`);
        loadDetail(selected);
    };

    const createLink = async () => {
        setBusy("link");
        try {
            const r = await api.post(`/trainings/${selected}/links`, { fragor: fragor.filter((f) => f.trim()), giltig_timmar: Number(timmar) });
            setNewLink(`${window.location.origin}/svara/${r.data.token}`);
            setFragor([""]);
            loadDetail(selected);
            toast.success("Deltagarlänk skapad");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(null);
        }
    };

    const closeLink = async (lid) => {
        await api.delete(`/links/${lid}`);
        loadDetail(selected);
    };

    const compile = async () => {
        setBusy("compile");
        try {
            await api.post(`/trainings/${selected}/compile`);
            toast.success("AI-sammanställning klar – granska utkastet");
            loadDetail(selected);
        } catch (err) {
            toast.error(formatApiError(err, "Sammanställningen misslyckades"));
        } finally {
            setBusy(null);
        }
    };

    const setDraftStatus = async (did, status) => {
        try {
            await api.put(`/drafts/${did}`, { status });
            toast.success(status === "godkand" ? "Sammanställning godkänd" : "Sammanställning avvisad");
            loadDetail(selected);
        } catch (err) {
            toast.error(formatApiError(err, "Kunde inte uppdatera utkastet"));
        }
    };

    const deleteTraining = async (tid) => {
        try {
            await api.delete(`/trainings/${tid}`);
            if (selected === tid) {
                setSelected(null);
                setDetail(null);
            }
            toast.success("Utbildning borttagen");
            loadList();
        } catch (err) {
            toast.error(formatApiError(err, "Kunde inte ta bort utbildningen"));
        }
    };

    const t = detail?.training;

    return (
        <div className="min-h-screen bg-abyss" data-testid="utbildning-page">
            <header className="h-14 bg-canvas/80 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-4">
                <Link data-testid="back-to-workspace" to="/" className="flex items-center gap-2 text-slate-400 hover:text-current transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-xs font-medium">Till kartan</span>
                </Link>
                <div className="flex items-center gap-2">
                    <img src="/hamn-emblem.png" alt="HAMN" className="w-7 h-7 rounded-full object-cover border border-current/30" />
                    <GraduationCap className="w-5 h-5 text-current" />
                    <h1 className="font-display font-extrabold tracking-tight text-slate-100">Utbildning & föreläsning</h1>
                </div>
                <div className="flex-1" />
                <button data-testid="new-training-button" onClick={() => setShowNew(true)}
                    className="flex items-center gap-2 text-xs font-semibold bg-current text-abyss rounded-lg px-3 py-1.5 hover:bg-tide transition-colors">
                    <Plus className="w-4 h-4" /> Ny utbildning
                </button>
            </header>

            <div className="flex flex-col md:flex-row min-h-[calc(100vh-3.5rem)]">
                <aside className="w-full md:w-[240px] xl:w-[300px] shrink-0 border-b md:border-b-0 md:border-r border-border p-4 space-y-2">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Mina utbildningar</p>
                    {trainings.length === 0 && <p className="text-sm text-slate-500">Inga utbildningar ännu.</p>}
                    {trainings.map((tr) => (
                        <div key={tr.id}
                            className={`flex items-stretch rounded-lg border transition-all ${selected === tr.id ? "border-current/60 bg-current/5" : "border-border bg-abyss/40 hover:border-current/30"}`}>
                            <button data-testid={`training-row-${tr.id}`} onClick={() => setSelected(tr.id)}
                                className="flex-1 text-left p-3 min-w-0">
                                <div className="text-sm font-semibold text-slate-200 truncate">{tr.title}</div>
                                <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mt-1">
                                    {tr.datum || "utan datum"} · {tr.antal_svar ?? 0} svar
                                </div>
                            </button>
                            <button data-testid={`training-delete-${tr.id}`} onClick={() => deleteTraining(tr.id)} title="Ta bort utbildning"
                                className="px-3 text-slate-600 hover:text-red-400 transition-colors">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </aside>

                <main className="flex-1 p-4 xl:p-6 overflow-y-auto panel-scroll">
                    {!t ? (
                        <div className="h-full flex flex-col items-center justify-center text-center" data-testid="utbildning-empty">
                            <Anchor className="w-8 h-8 text-slate-700 mb-3" />
                            <p className="text-sm text-slate-500 max-w-sm leading-relaxed">
                                Välj eller skapa en utbildning. Ladda upp material, skicka tillfälliga länkar med frågor till deltagarna och låt AI sammanställa svaren – som utkast du granskar.
                            </p>
                        </div>
                    ) : (
                        <div className="max-w-3xl space-y-6">
                            <div>
                                <h2 className="font-display font-bold text-2xl text-slate-100">{t.title}</h2>
                                <p className="text-sm text-slate-500 mt-1">{t.datum} {t.description && `· ${t.description}`}</p>
                            </div>

                            <section className="glass-panel rounded-xl p-5">
                                <h3 className="font-display font-bold text-slate-100 mb-3">Material</h3>
                                <div className="space-y-2 mb-3">
                                    {detail.materials.length === 0 && <p className="text-xs text-slate-500">Inget material uppladdat.</p>}
                                    {detail.materials.map((m) => (
                                        <div key={m.id} data-testid={`material-row-${m.id}`} className="flex items-center justify-between rounded-lg border border-border bg-abyss/40 px-3 py-2">
                                            <span className="text-sm text-slate-300 truncate">{m.filnamn}</span>
                                            <div className="flex items-center gap-2">
                                                <a data-testid={`material-download-${m.id}`} href={`${API}/training-materials/${m.id}/download`}
                                                    className="text-current hover:text-tide transition-colors"><FileDown className="w-4 h-4" /></a>
                                                <button data-testid={`material-delete-${m.id}`} onClick={() => deleteMaterial(m.id)}
                                                    className="text-slate-600 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                    ))}
                                    {busy === "upload" && (
                                        <div data-testid="material-upload-pending" className="flex items-center gap-2 rounded-lg border border-dashed border-current/30 px-3 py-2 text-xs text-slate-500">
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laddar upp material …
                                        </div>
                                    )}
                                </div>
                                <label data-testid="material-upload-label"
                                    className="flex items-center justify-center gap-2 border border-dashed border-border rounded-lg py-3 text-xs text-slate-400 hover:border-current/40 hover:text-current cursor-pointer transition-colors">
                                    {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                    Ladda upp material (PDF, bilder, dokument – max 25 MB)
                                    <input data-testid="material-upload-input" type="file" className="hidden" onChange={uploadFile} />
                                </label>
                            </section>

                            <section className="glass-panel rounded-xl p-5">
                                <h3 className="font-display font-bold text-slate-100 mb-1">Deltagarlänkar</h3>
                                <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                                    Tillfälliga, anonyma länkar med uppgifter och frågor. Deltagarna svarar utan inloggning – svaren pseudonymiseras (Deltagare 1, 2, …).
                                </p>
                                <div className="space-y-2 mb-4">
                                    {detail.links.map((l) => (
                                        <div key={l.id} data-testid={`link-row-${l.id}`} className="rounded-lg border border-border bg-abyss/40 p-3">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${l.aktiv ? "text-buoy border-buoy/40 bg-buoy/10" : "text-slate-500 border-slate-600"}`}>
                                                    {l.aktiv ? "Aktiv" : "Stängd"}
                                                </span>
                                                <span className="font-mono text-[10px] text-slate-500">
                                                    {l.antal_svar} svar · utgår {new Date(l.expires_at).toLocaleString("sv-SE")}
                                                </span>
                                                {l.aktiv && (
                                                    <button data-testid={`link-close-${l.id}`} onClick={() => closeLink(l.id)}
                                                        className="text-slate-500 hover:text-red-400 text-xs transition-colors">Stäng</button>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-2">
                                                <code className="flex-1 font-mono text-[10px] text-slate-400 truncate">{window.location.origin}/svara/{l.token}</code>
                                                <button data-testid={`link-copy-${l.id}`}
                                                    onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/svara/${l.token}`); toast.success("Länk kopierad"); } catch { toast.error("Kunde inte kopiera länken"); } }}
                                                    className="text-current hover:text-tide transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="border-t border-border pt-4 space-y-2">
                                    <p className={labelCls}>Ny länk – frågor/uppgifter till deltagarna</p>
                                    {fragor.map((f, i) => (
                                        <div key={i} className="flex gap-2">
                                            <input data-testid={`link-question-${i}`} className={inputCls} value={f}
                                                placeholder={`Fråga ${i + 1}, t.ex. "Vad tar du med dig från passet?"`}
                                                onChange={(e) => setFragor(fragor.map((x, j) => (j === i ? e.target.value : x)))} />
                                            {fragor.length > 1 && (
                                                <button data-testid={`link-question-remove-${i}`} onClick={() => setFragor(fragor.filter((_, j) => j !== i))}
                                                    className="text-slate-600 hover:text-red-400"><X className="w-4 h-4" /></button>
                                            )}
                                        </div>
                                    ))}
                                    <div className="flex gap-2">
                                        <button data-testid="link-add-question" onClick={() => setFragor([...fragor, ""])}
                                            className="text-xs text-current hover:text-tide transition-colors flex items-center gap-1">
                                            <Plus className="w-3.5 h-3.5" /> Lägg till fråga
                                        </button>
                                        <div className="flex-1" />
                                        <select data-testid="link-validity-select" className="bg-abyss/60 border border-border rounded-lg px-2 py-1 text-xs text-slate-300 outline-none"
                                            value={timmar} onChange={(e) => setTimmar(e.target.value)}>
                                            <option value={24}>Giltig 24 timmar</option>
                                            <option value={72}>Giltig 3 dygn</option>
                                            <option value={168}>Giltig 7 dygn</option>
                                        </select>
                                    </div>
                                    <button data-testid="create-link-button" onClick={createLink} disabled={busy === "link" || !fragor.some((f) => f.trim())}
                                        className="w-full flex items-center justify-center gap-2 bg-current/15 border border-current/40 text-current rounded-lg py-2 text-sm font-semibold hover:bg-current/25 transition-colors disabled:opacity-40">
                                        {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                                        Skapa deltagarlänk
                                    </button>
                                    {newLink && (
                                        <div data-testid="new-link-result" className="rounded-lg border border-buoy/40 bg-buoy/10 p-3 flex items-center gap-2">
                                            <code className="flex-1 font-mono text-xs text-buoy truncate">{newLink}</code>
                                            <button data-testid="new-link-copy" onClick={async () => { try { await navigator.clipboard.writeText(newLink); toast.success("Länk kopierad"); } catch { toast.error("Kunde inte kopiera länken"); } }}
                                                className="text-buoy hover:text-emerald-300"><Copy className="w-4 h-4" /></button>
                                        </div>
                                    )}
                                </div>
                            </section>

                            <section className="glass-panel rounded-xl p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-display font-bold text-slate-100">Svar & AI-sammanställning</h3>
                                    <button data-testid="compile-button" onClick={compile} disabled={busy === "compile" || detail.responses.length === 0}
                                        className="flex items-center gap-2 bg-aidraft/15 border border-aidraft/40 text-aidraft rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-aidraft/25 transition-colors disabled:opacity-40">
                                        {busy === "compile" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GraduationCap className="w-3.5 h-3.5" />}
                                        Sammanställ med AI ({detail.responses.length} svar)
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {detail.drafts.map((d) => <CompileDraft key={d.id} draft={d} onStatus={setDraftStatus} />)}
                                </div>
                                {detail.responses.length > 0 && (
                                    <button data-testid="toggle-responses" onClick={() => setOpenResponses(!openResponses)}
                                        className="text-xs text-slate-500 hover:text-slate-300 mt-4 transition-colors">
                                        {openResponses ? "Dölj" : "Visa"} råsvar ({detail.responses.length})
                                    </button>
                                )}
                                {openResponses && (
                                    <div className="space-y-2 mt-2">
                                        {detail.responses.map((r) => (
                                            <div key={r.id} data-testid={`response-row-${r.id}`} className="rounded-lg border border-border bg-abyss/40 p-3">
                                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">{r.pseudonym} · {new Date(r.created_at).toLocaleString("sv-SE")}</p>
                                                {r.svar.map((s, i) => (
                                                    <div key={i} className="mt-1">
                                                        <p className="text-[11px] font-semibold text-slate-300">{s.fraga}</p>
                                                        <p className="text-xs text-slate-400">{s.svar}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </div>
                    )}
                </main>
            </div>

            {showNew && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="training-dialog">
                    <div className="absolute inset-0 bg-abyss/70 backdrop-blur-sm" onClick={() => setShowNew(false)} />
                    <div className="relative glass-panel rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
                        <h3 className="font-display font-bold text-lg text-slate-100">Ny utbildning / föreläsning</h3>
                        <input data-testid="training-title-input" className={inputCls} value={newForm.title} placeholder="Titel, t.ex. 'Flödesanalys – grundpass'"
                            onChange={(e) => setNewForm({ ...newForm, title: e.target.value })} />
                        <input data-testid="training-date-input" className={inputCls} value={newForm.datum} placeholder="Datum, t.ex. 2026-06-15"
                            onChange={(e) => setNewForm({ ...newForm, datum: e.target.value })} />
                        <textarea data-testid="training-desc-input" className={inputCls} rows={2} value={newForm.description} placeholder="Kort beskrivning"
                            onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} />
                        <div className="flex gap-2">
                            <button data-testid="training-create-submit" onClick={createTraining} disabled={!newForm.title.trim()}
                                className="flex-1 bg-current text-abyss font-semibold text-sm rounded-lg py-2.5 hover:bg-tide transition-colors disabled:opacity-50">
                                Skapa
                            </button>
                            <button data-testid="training-create-cancel" onClick={() => setShowNew(false)}
                                className="px-4 border border-border text-slate-400 rounded-lg text-sm hover:border-slate-500 transition-colors">
                                Avbryt
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
